import React, { useState } from 'react';
import {
  Dialog,
  DialogContent,
  IconButton,
  Box,
  CircularProgress,
  Tooltip,
  Button,
} from '@mui/material';
import { Close as CloseIcon, Download, Email as EmailIcon } from '@mui/icons-material';
import { FaWhatsapp } from 'react-icons/fa';
import { colors } from '../theme/modernTheme';

/**
 * Receipt Preview Modal
 *
 * Reusable full-screen modal that shows a receipt preview with action buttons:
 * - Download as PDF
 * - Send via Email
 * - Send via WhatsApp
 *
 * @param {Object} props
 * @param {boolean} props.open - Whether modal is open
 * @param {Function} props.onClose - Callback when modal is closed
 * @param {string} props.content - HTML content to render
 * @param {Function} props.onDownload - Download PDF handler
 * @param {Function} props.onSendEmail - Send email handler
 * @param {Function} props.onSendWhatsApp - Send WhatsApp handler
 * @param {boolean} [props.hasEmail] - Whether donor has email
 * @param {boolean} [props.hasPhone] - Whether donor has phone
 * @param {string} [props.donorName] - Donor name for display
 */
export default function ReceiptPreviewModal({
  open,
  onClose,
  content,
  onDownload,
  onSendEmail,
  onSendWhatsApp,
  hasEmail = false,
  hasPhone = false,
  emailTemplate = '',
  whatsappTemplate = '',
}) {
  const [actionLoading, setActionLoading] = useState(null); // 'download' | 'email' | 'whatsapp' | null

  const handleAction = async (type, handler) => {
    if (!handler) return;
    try {
      setActionLoading(type);
      await handler();
    } catch (error) {
      console.error(`Error performing ${type}:`, error);
    } finally {
      setActionLoading(null);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth={false}
      fullScreen
      PaperProps={{
        sx: {
          backgroundColor: colors.background.default,
        },
      }}
    >
      {/* Close Button - Top Left */}
      <IconButton
        onClick={onClose}
        sx={{
          position: 'fixed',
          top: 16,
          left: 16,
          zIndex: 1300,
          color: colors.text.primary,
          fontWeight: 900,
          '&:hover': {
            backgroundColor: 'transparent !important',
            color: colors.text.dark,
          },
          fontSize: '24px !important',
        }}
      >
        <CloseIcon sx={{ fontSize: '16px !important' }} />
      </IconButton>

      {/* Action Buttons - Top Right */}
      <Box
        sx={{
          position: 'fixed',
          top: 16,
          right: 16,
          zIndex: 1300,
          display: 'flex',
          alignItems: 'center',
          gap: 1,
        }}
      >
        {/* Download PDF */}
        <Tooltip title="Download Receipt (A5) : Header and footer image will not be included" arrow>
          <Button
            onClick={() => handleAction('download', onDownload)}
            disabled={actionLoading !== null}
            startIcon={
              actionLoading === 'download' ? (
                <CircularProgress size={16} color="inherit" />
              ) : (
                <Download sx={{ fontSize: '17px !important' }} />
              )
            }
            sx={{
              borderRadius: '4px',
              textTransform: 'none',
              fontWeight: 600,
              fontSize: '14px !important',
              px: 1.5,
              border: `1px solid ${actionLoading ? colors.text.disabled : colors.primary.main}`,
              color: colors.primary.main,
              '&:hover': {
                borderColor: colors.primary.dark,
                backgroundColor: `${colors.primary.main}10`,
              },
              '&.Mui-disabled': {
                color: colors.text.disabled,
              },
            }}
          >
            {actionLoading === 'download' ? 'Downloading...' : 'Download'}
          </Button>
        </Tooltip>

        {/* Send Email */}
        <Tooltip
          title={
            !hasEmail
              ? 'Cannot send — email not available'
              : emailTemplate
                ? 'Send via Email'
                : 'Email template is not available. Please configure it in Trust settings'
          }
          arrow
        >
          <span>
            <Button
              onClick={() => handleAction('email', onSendEmail)}
              disabled={actionLoading !== null || !hasEmail || !emailTemplate}
              startIcon={
                actionLoading === 'email' ? (
                  <CircularProgress size={16} color="inherit" />
                ) : (
                  <EmailIcon sx={{ fontSize: '17px !important' }} />
                )
              }
              sx={{
                borderRadius: '4px',
                textTransform: 'none',
                fontWeight: 600,
                fontSize: '14px !important',
                px: 1.5,
                border: `1px solid ${actionLoading || !hasEmail || !emailTemplate ? colors.text.disabled : colors.primary.main}`,
                color: hasEmail && emailTemplate ? colors.primary.main : colors.text.disabled,
                '&:hover': {
                  backgroundColor: `${colors.primary.main}10`,
                  color: hasEmail && emailTemplate ? colors.primary.dark : colors.text.disabled,
                },
                '&.Mui-disabled': {
                  color: colors.text.disabled,
                },
              }}
            >
              {actionLoading === 'email' ? 'Sending...' : 'Email'}
            </Button>
          </span>
        </Tooltip>

        {/* Send WhatsApp */}
        <Tooltip
          title={
            !hasPhone
              ? 'Cannot send — phone not available'
              : whatsappTemplate
                ? 'Send via WhatsApp'
                : 'WhatsApp template is not available. Please configure it in Trust settings'
          }
          arrow
        >
          <span>
            <Button
              onClick={() => handleAction('whatsapp', onSendWhatsApp)}
              disabled={actionLoading !== null || !hasPhone || !whatsappTemplate}
              startIcon={
                actionLoading === 'whatsapp' ? (
                  <CircularProgress size={16} color="inherit" />
                ) : (
                  <FaWhatsapp size={17} />
                )
              }
              sx={{
                borderRadius: '4px',
                textTransform: 'none',
                fontWeight: 600,
                fontSize: '14px !important',
                px: 1.5,
                border: `1px solid ${actionLoading || !hasPhone || !whatsappTemplate ? colors.text.disabled : colors.primary.main}`,
                color: hasPhone && whatsappTemplate ? colors.primary.main : colors.text.disabled,
                '&:hover': {
                  backgroundColor: `${colors.primary.main}10`,
                  color: hasPhone && whatsappTemplate ? colors.primary.dark : colors.text.disabled,
                },
                '&.Mui-disabled': {
                  color: colors.text.disabled,
                },
              }}
            >
              {actionLoading === 'whatsapp' ? 'Sending...' : 'WhatsApp'}
            </Button>
          </span>
        </Tooltip>
      </Box>

      {/* Dialog Content - Preview Area */}
      <DialogContent
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'flex-start',
          p: 4,
          overflow: 'auto',
        }}
      >
        {/* Preview Container with A5 dimensions */}
        <Box
          sx={{
            width: '148mm',
            height: '210mm',
            backgroundColor: '#ffffff',
            boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)',
            overflow: 'hidden',
            '& *': {
              border: 0,
              boxSizing: 'border-box',
            },
            '& .receipt': {
              width: '100%',
              height: '100%',
              border: 0,
              margin: 0,
              padding: 0,
            },
          }}
          dangerouslySetInnerHTML={{ __html: content }}
        />
      </DialogContent>
    </Dialog>
  );
}
